import { memo, useMemo } from 'react';
import { Checkbox, FormControlLabel, Box, ToggleButtonGroup, ToggleButton, Tooltip } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { FieldValue } from '../../../../../types/QueryBuilder';
import { useLanguage } from '../../../../../context/LanguageContext';
import CheckboxListPicker from './CheckboxListPicker';
import { formatSelectedCount } from './selectedCountLabel';

interface CheckboxEditorProps {
    ruleId: string;
    value: string | string[] | number | null;
    values: FieldValue[];
    onChange: (value: string | string[]) => void;
    compact?: boolean;
}

const BOOLEAN_ACCEPT_VALUE = '1';
const BOOLEAN_REFUSE_VALUE = '0';
const BOOLEAN_NO_PREFERENCE_VALUE = '';

// Defined once at module scope: MUI/emotion recomputes and re-injects a component's styles whenever
// it receives a new `sx` object reference, so inlining these literals in the JSX below regenerated
// them (and reprocessed the styles) on every keystroke anywhere in the form, which is what caused
// the perceived input lag on this toggle group.
const ACCEPT_BUTTON_SX = {
    cursor: 'pointer',
    '&:hover': { bgcolor: 'rgba(40, 167, 69, 0.12)' },
    '&.Mui-selected': { bgcolor: '#28a745', color: 'white', '&:hover': { bgcolor: '#218838' } },
};
const REFUSE_BUTTON_SX = {
    cursor: 'pointer',
    '&:hover': { bgcolor: 'rgba(220, 53, 69, 0.12)' },
    '&.Mui-selected': { bgcolor: '#dc3545', color: 'white', '&:hover': { bgcolor: '#c82333' } },
};
const NO_PREFERENCE_BUTTON_SX = {
    cursor: 'pointer',
    '&:hover': { bgcolor: 'rgba(85, 110, 230, 0.12)' },
    '&.Mui-selected': { bgcolor: '#556ee6', color: 'white', '&:hover': { bgcolor: '#4457c9' } },
};
const COMPACT_GRID_SX = { display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, columnGap: 1 };
const TOOLTIP_LABEL_SX = { display: 'flex', alignItems: 'center', gap: 0.5 };
const TOOLTIP_ICON_SX = { fontSize: '1rem', color: 'text.secondary' };
const FORM_CONTROL_LABEL_SX = { m: 0 };

function CheckboxEditor({ ruleId, value, values, onChange, compact = false }: CheckboxEditorProps) {
    const { t } = useLanguage();
    const selectedValues = useMemo(
        () => (Array.isArray(value) ? value : (value ? [String(value)] : [])),
        [value]
    );
    const selectedSet = useMemo(() => new Set(selectedValues), [selectedValues]);

    if (values.length === 0) {
        return (
            <ToggleButtonGroup
                size="small"
                exclusive
                value={String(value ?? BOOLEAN_NO_PREFERENCE_VALUE)}
                onChange={(_event, newValue) => onChange(newValue ?? BOOLEAN_NO_PREFERENCE_VALUE)}
            >
                <ToggleButton value={BOOLEAN_ACCEPT_VALUE} sx={ACCEPT_BUTTON_SX}>
                    {t('pages.clients.queryBuilder.simple.optin.accepts')}
                </ToggleButton>
                <ToggleButton value={BOOLEAN_REFUSE_VALUE} sx={REFUSE_BUTTON_SX}>
                    {t('pages.clients.queryBuilder.simple.optin.refuses')}
                </ToggleButton>
                <ToggleButton value={BOOLEAN_NO_PREFERENCE_VALUE} sx={NO_PREFERENCE_BUTTON_SX}>
                    {t('pages.clients.queryBuilder.simple.optin.noPreference')}
                </ToggleButton>
            </ToggleButtonGroup>
        );
    }

    const handleChange = (optionName: string, checked: boolean) => {
        const newValues = checked
            ? [...selectedValues, optionName]
            : selectedValues.filter(v => v !== optionName);
        onChange(newValues);
    };

    const handleOptionChange = (option: FieldValue, checked: boolean) => {
        const names = option.mergedNames ?? [option.name];
        const newValues = checked
            ? [...selectedValues.filter(v => !names.includes(v)), ...names]
            : selectedValues.filter(v => !names.includes(v));
        onChange(newValues);
    };

    if (compact) {
        return (
            <Box sx={COMPACT_GRID_SX}>
                {values.map((option, idx) => {
                    const isChecked = (option.mergedNames ?? [option.name]).some(name => selectedSet.has(name));
                    const label = option.tooltip ? (
                        <Box sx={TOOLTIP_LABEL_SX}>
                            {option.label}
                            <Tooltip title={option.tooltip} arrow>
                                <InfoOutlinedIcon sx={TOOLTIP_ICON_SX} />
                            </Tooltip>
                        </Box>
                    ) : option.label;

                    return (
                        <FormControlLabel
                            key={`${ruleId}-compact-${idx}`}
                            sx={FORM_CONTROL_LABEL_SX}
                            control={
                                <Checkbox
                                    checked={isChecked}
                                    onChange={(e) => handleOptionChange(option, e.target.checked)}
                                    size="small"
                                />
                            }
                            label={label}
                        />
                    );
                })}
            </Box>
        );
    }

    return (
        <CheckboxListPicker
            values={values}
            selectedValues={selectedValues}
            onToggleOption={handleChange}
            onToggleSelectAll={(allSelected) => onChange(allSelected ? [] : values.map(option => option.name))}
            searchPlaceholder={t('pages.clients.queryBuilder.searchPlaceholder')}
            selectAllLabel={t('pages.clients.queryBuilder.simple.selectAll')}
            noOptionsLabel={t('pages.clients.queryBuilder.simple.noOptionsFound')}
            selectedCountLabel={(count) => formatSelectedCount(t, count)}
        />
    );
}

export default memo(CheckboxEditor);
